import type { LegalDoc } from "./types";

// Nederlands (AVG). Vertaling van het referentiebeleid (v1.0, 1 sept. 2026).
const gdprNl: LegalDoc = {
  title: "Privacy- en gegevensbeschermingsbeleid (GDPR / RGPD)",
  effectiveDate: "1 november 2026",
  version: "1.1",
  intro:
    "Dit beleid legt uit hoe Photohawk persoonsgegevens verwerkt op grond van de Algemene Verordening Gegevensbescherming van de EU (GDPR) en het Franse equivalent daarvan, de RGPD (Règlement Général sur la Protection des Données), evenals de UK GDPR. RGPD en GDPR verwijzen naar dezelfde wetgeving.",
  sections: [
    {
      heading: "1. Wie wij zijn",
      blocks: [
        {
          type: "p",
          text: "Photohawk is een platform voor evenementenfotografie waarmee fotografen en organisatoren evenementenfoto's kunnen publiceren, doorzoeken en verkopen.",
        },
        {
          type: "ul",
          items: [
            "Juridische entiteit: CLOUDCONCUR LTD (bedrijfsnummer 13399913), een in Engeland en Wales geregistreerde onderneming, handelend onder de naam Photohawk.",
            "Statutair adres: 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Verenigd Koninkrijk.",
            "Functionaris voor gegevensbescherming (DPO): Andrew Hammond — contact support@photohawk.com.",
            "Vertegenwoordiger in de EU (Art. 27 GDPR): wij hebben geen vertegenwoordiger in de EU aangewezen, op grond van het feit dat onze verwerking van gegevens van EU-inwoners incidenteel is en niet onze hoofdactiviteit vormt (Art. 27(2)). Wij evalueren dit regelmatig en zullen een vertegenwoordiger aanwijzen indien onze verwerking wijzigt.",
            "Toezichthoudende autoriteiten: het Information Commissioner's Office (ICO) voor het Verenigd Koninkrijk, en EU-autoriteiten waaronder de Franse CNIL voor personen in de EU.",
          ],
        },
      ],
    },
    {
      heading: "2. Onze twee rollen — verwerkingsverantwoordelijke en verwerker",
      blocks: [
        {
          type: "p",
          text: "Photohawk heeft te maken met twee soorten personen, en onze juridische rol verschilt voor elk daarvan:",
        },
        {
          type: "ul",
          items: [
            "Fotografen en organisatoren (onze klanten). Voor uw accountgegevens (naam, e-mail, bedrijfsgegevens, inloggegevens, facturerings- en uitbetalingsgegevens) is Photohawk de verwerkingsverantwoordelijke.",
            "Deelnemers en kopers aan evenementen (uw klanten). Voor de gegevens van de personen die op de foto's staan en deze kopen, bent u (de fotograaf/organisator) de verwerkingsverantwoordelijke en is Photohawk uw verwerker — wij verwerken die gegevens volgens uw instructies om de galerijen, het zoeken en de verkoop die u heeft ingesteld te laten werken. Een verwerkersovereenkomst (zie §11) regelt deze relatie.",
          ],
        },
      ],
    },
    {
      heading: "3. Welke persoonsgegevens wij verwerken",
      blocks: [
        { type: "p", text: "Voor fotografen/organisatoren (wij zijn verwerkingsverantwoordelijke):" },
        {
          type: "ul",
          items: [
            "Identiteit en contact: naam, e-mail, telefoon, bedrijfsnaam, adres.",
            "Account en beveiliging: inloggegevens (via AWS), sessies.",
            "Financieel: abonnements-/factureringsgegevens (via Stripe) en uitbetalingsgegevens (via Trolley).",
          ],
        },
        { type: "p", text: "Voor deelnemers/kopers aan evenementen (wij zijn verwerker, namens de organisator):" },
        {
          type: "ul",
          items: [
            "Identiteit en contact: naam, e-mail, telefoonnummer, post-/factuuradres.",
            "Foto's van deelnemers.",
            "Biometrische gegevens — wanneer zoeken op gezicht/selfie is ingeschakeld (zie §4).",
            "Bestel- en betalingsgegevens (verwerkt via onze betalingsproviders; wij bewaren geen volledige kaartnummers).",
            "Status van marketingtoestemming, wanneer de deelnemer zich hiervoor aanmeldt.",
          ],
        },
      ],
    },
    {
      heading: "4. Zoeken op gezicht en selfie (bijzondere categorie biometrische gegevens)",
      blocks: [
        {
          type: "p",
          text: "Wanneer een organisator zoeken op selfie/gezicht inschakelt, maakt Photohawk een wiskundige weergave van gezichten aan zodat een deelnemer zijn of haar eigen foto's kan vinden.",
        },
        {
          type: "p",
          text: "Zoeken op gezicht is optioneel. We bewaren gezichtsgegevens (numerieke gezichtssjablonen, geen foto's) alleen zolang de galerij waaruit ze afkomstig zijn beschikbaar is voor zoeken op gezicht, en verwijderen ze definitief uiterlijk 12 maanden nadat de foto is geüpload — of binnen 30 dagen als de galerij wordt gesloten of zoeken op gezicht wordt uitgeschakeld, of als u ons vraagt ze te verwijderen of uw toestemming intrekt. Selfies die worden geüpload om te zoeken, worden alleen voor die zoekopdracht gebruikt en niet bewaard.",
        },
        {
          type: "link",
          text: "Alle details:",
          label: "Beleid voor het bewaren van biometrische gegevens",
          href: "/biometric-data-retention",
        },
        {
          type: "ul",
          items: [
            "Doel: uitsluitend om een persoon te koppelen aan zijn of haar eigen evenementenfoto's.",
            "Waar: verwerkt en opgeslagen in de Europese Unie (AWS, regio Ierland).",
            "Bewaring: gezichtsgegevens worden uiterlijk 12 maanden na het uploaden van de foto verwijderd, binnen 30 dagen nadat de galerij is gesloten of zoeken op gezicht is uitgeschakeld, en binnen 30 dagen na een verzoek tot verwijdering of het intrekken van toestemming — wat zich het eerst voordoet (zie het beleid voor het bewaren van biometrische gegevens).",
            "Rechtsgrond (Art. 9 GDPR): biometrische gegevens vormen een bijzondere categorie en krijgen extra bescherming: ze worden alleen gebruikt zodat mensen hun eigen foto's in die galerij kunnen vinden. Als verwerkingsverantwoordelijke voor de deelnemers beslist de beheerder van de galerij of zoeken op gezicht passend is voor zijn evenement en toegestaan is waar het plaatsvindt. Waar de lokale wet de toestemming van de gefotografeerde personen vereist, moet de beheerder die hebben, bijvoorbeeld via de inschrijving voor het evenement; heeft hij die niet, dan moet hij in plaats daarvan zoeken op startnummer en tijd gebruiken, waarvoor geen gezichtsgegevens nodig zijn. Photohawk biedt een geïntegreerde toestemmingsstap: een deelnemer wordt gevraagd toestemming te geven voordat zijn of haar selfie voor zoeken op gezicht wordt gebruikt, en kan weigeren.",
            "Minderjarigen: zie §13.",
          ],
        },
      ],
    },
    {
      heading: "5. Waarom wij gegevens gebruiken en onze rechtsgronden",
      blocks: [
        {
          type: "ul",
          items: [
            "Om de dienst te leveren (galerijen hosten, zoeken uitvoeren, bestellingen verwerken): uitvoering van een overeenkomst.",
            "Betalingen, uitbetalingen, fiscale en boekhoudkundige administratie: uitvoering van een overeenkomst en naleving van een wettelijke verplichting.",
            "Zoeken op gezicht/selfie (Art. 9): de uitdrukkelijke toestemming van de persoon die zoekt, voordat zijn of haar selfie wordt gebruikt; voor de gefotografeerde personen zoals beschreven in §4.",
            "Marketing-e-mails/-berichten: uitsluitend met toestemming van de ontvanger (opt-in), die te allen tijde kan worden ingetrokken.",
            "Analyse- en advertentiecookies: uitsluitend met uw toestemming, gegeven via onze cookiebanner (zie §12).",
            "Beveiliging, fraudepreventie en verbetering van de dienst: ons gerechtvaardigd belang, afgewogen tegen uw rechten.",
          ],
        },
      ],
    },
    {
      heading: "6. Waar uw gegevens worden opgeslagen en internationale doorgiften",
      blocks: [
        {
          type: "ul",
          items: [
            "Gegevenslocatie: gegevens van deelnemers en accounts — waaronder foto's en biometrische gezichtsgegevens — worden gehost in de Europese Unie (AWS, Ierland / eu-west-1). Onze identiteitsdienst (AWS) bevindt zich eveneens in de EU.",
            "Internationale doorgiften: sommige van onze subverwerkers (zie §7) opereren buiten de EU/het VK (bijv. in de Verenigde Staten). Wanneer persoonsgegevens buiten de EU/het VK worden doorgegeven, baseren wij ons op een adequaatheidsbesluit waar er een van toepassing is, en anders op de modelcontractbepalingen (SCC's) van de Europese Commissie samen met het Britse International Data Transfer Addendum, met aanvullende waarborgen waar nodig.",
          ],
        },
      ],
    },
    {
      heading: "7. Subverwerkers",
      blocks: [
        {
          type: "p",
          text: "Wij maken gebruik van een klein aantal zorgvuldig geselecteerde providers om de dienst te leveren, elk gebonden aan een verwerkersovereenkomst (en aan de SCC's/het UK Addendum wanneer zij gegevens buiten de EU/het VK verwerken):",
        },
        {
          type: "table",
          head: ["Provider", "Doel", "Betrokken gegevens"],
          rows: [
            ["Amazon Web Services (AWS)", "Hosting, opslag, identiteit, zoeken op gezicht, transactionele e-mail — EU-regio", "Alle persoonsgegevens, foto's, biometrische gegevens"],
            ["Cloudflare (incl. R2)", "CDN, aangepaste domeinen, objectopslag", "Foto's, verzoekgegevens"],
            ["Stripe", "Kaartbetalingen en abonnementen", "Betalingsgegevens van kopers en klanten"],
            ["Xendit", "Betalingen (Zuidoost-Azië)", "Betalingsgegevens van kopers"],
            ["Paystack", "Betalingen (Afrika)", "Betalingsgegevens van kopers"],
            ["Trolley", "Uitbetalingen aan fotografen", "Identiteit en bankgegevens van de begunstigde"],
            ["Postmark", "Transactionele e-mail", "Namen, e-mailadressen"],
            ["Twilio / WhatsApp", "Levering van foto's en meldingen", "Namen, telefoonnummers"],
            ["Google (Google Analytics)", "Analyse van websitegebruik (op basis van toestemming)", "Gebruiks-/apparaatgegevens, identificatoren"],
            ["Meta Platforms (Meta Pixel)", "Advertenties en conversiemeting (op basis van toestemming)", "Gebruiksgegevens, identificatoren"],
          ],
        },
        { type: "p", text: "Een actuele lijst is op verzoek beschikbaar." },
      ],
    },
    {
      heading: "8. Hoe lang wij gegevens bewaren (bewaartermijn)",
      blocks: [
        {
          type: "ul",
          items: [
            "Biometrische / gezichtsgegevens: verwijderd uiterlijk 12 maanden na het uploaden van de foto, of eerder zoals beschreven in §4 en in het beleid voor het bewaren van biometrische gegevens. Selfies die voor een zoekopdracht worden gebruikt, worden niet bewaard.",
            "Foto's en video's: de bewaring wordt beheerd door de accounthouder (de organisator/fotograaf). Inhoud wordt bewaard zolang de accounthouder deze bewaart; accounthouders kunnen foto's, video's, galerijen en deelnemersgegevens te allen tijde verwijderen, en wij verwijderen deze dan. Photohawk legt geen vaste levensduur op aan deze inhoud.",
            "Bestellingen en financiële administratie: 7 jaar bewaard om te voldoen aan fiscale en boekhoudkundige verplichtingen.",
            "Contactgegevens van deelnemers/kopers: beheerd door de organisator — bewaard zolang de organisator deze bewaart, en verwijderd wanneer de organisator deze verwijdert of op een geverifieerd verzoek tot wissing.",
            "Marketinglijsten: bewaard totdat de toestemming wordt ingetrokken.",
            "Operationele/beveiligingslogboeken worden 30 dagen bewaard.",
          ],
        },
      ],
    },
    {
      heading: "9. Hoe wij gegevens beschermen",
      blocks: [
        {
          type: "ul",
          items: [
            "Tijdens verzending: overal versleuteld via HTTPS/TLS.",
            "In rust: foto's en inhoud worden opgeslagen op AWS en Cloudflare R2, die gegevens standaard in rust versleutelen; gevoelige inloggegevens (bijv. tokens van gekoppelde accounts) worden bovendien versleuteld met AWS.",
            "Toegangscontroles: geauthenticeerde toegang via AWS; toegang van personeel is beperkt tot wat nodig is om de dienst te exploiteren.",
          ],
        },
      ],
    },
    {
      heading: "10. Uw rechten",
      blocks: [
        {
          type: "p",
          text: "Op grond van de GDPR/RGPD en de UK GDPR heeft u het recht om: uw gegevens in te zien; deze te corrigeren; deze te wissen (“recht om vergeten te worden”); de verwerking te beperken of hiertegen bezwaar te maken; gegevensoverdraagbaarheid; en uw toestemming te allen tijde in te trekken. U heeft eveneens het recht om een klacht in te dienen bij een toezichthoudende autoriteit (het ICO in het VK, of uw lokale autoriteit zoals de CNIL in Frankrijk).",
        },
        { type: "p", text: "Hoe deze uit te oefenen:" },
        {
          type: "ul",
          items: [
            "Fotografen/organisatoren (wij zijn verwerkingsverantwoordelijke): neem contact op met onze DPO via support@photohawk.com. Wij reageren binnen één maand.",
            "Deelnemers/kopers aan evenementen (de organisator is verwerkingsverantwoordelijke): neem contact op met de fotograaf of organisator die uw evenement heeft geleid. Fotografen kunnen de persoonsgegevens van een deelnemer — waaronder hun foto's en eventuele gegevens voor zoeken op gezicht — vanuit Photohawk verwijderen, en Photohawk zal een geverifieerd verzoek namens de organisator uitvoeren.",
            "Gezichtsgegevens: vraag om verwijdering (of trek uw toestemming in) via „Verzoek indienen” in het zoeken op selfie van de galerij, of via support@photohawk.com. We handelen het verzoek binnen 30 dagen af.",
          ],
        },
        {
          type: "p",
          text: "Het sluiten van een Photohawk-account beëindigt de facturering, maar wist op zichzelf niet de opgeslagen gegevens; verzoek ons om gegevens te wissen als dat is wat u nodig heeft.",
        },
      ],
    },
    {
      heading: "11. Verwerkersovereenkomst (DPA)",
      blocks: [
        {
          type: "p",
          text: "Voor fotografen/organisatoren wordt onze verwerking van de gegevens van uw deelnemers geregeld door een verwerkersovereenkomst op grond van art. 28 GDPR, die wij op verzoek beschikbaar stellen via support@photohawk.com.",
        },
      ],
    },
    {
      heading: "12. Cookies en vergelijkbare technologieën",
      blocks: [
        {
          type: "ul",
          items: [
            "Strikt noodzakelijke cookies (voor inloggen, beveiliging en kernfunctionaliteit) zijn altijd actief; hiervoor is geen toestemming vereist.",
            "Analyse- en advertentiecookies — Google Analytics en de Meta Pixel (zie §7) — zijn niet-essentieel en laden pas nadat u zich heeft aangemeld via onze cookietoestemmingsbanner. U kunt uw keuze te allen tijde wijzigen of intrekken via de banner / cookie-instellingen. Weigeren laat de dienst volledig bruikbaar.",
            "Meer details zijn te vinden in ons afzonderlijke Cookiebeleid.",
          ],
        },
      ],
    },
    {
      heading: "13. Gegevens van kinderen",
      blocks: [
        {
          type: "p",
          text: "Evenementen kunnen minderjarigen omvatten. Of zoeken op gezicht passend is voor een evenement, beslist de beheerder van de galerij (zie §4); wanneer minderjarigen aanwezig kunnen zijn en de lokale wet de toestemming van de gefotografeerde personen vereist, moet de beheerder voor hen toestemming van een ouder of voogd hebben, of in plaats daarvan zoeken op startnummer en tijd gebruiken. Photohawk maakt niet bewust biometrische gegevens van een kind aan zonder een wettelijk vereiste toestemming, en de geïntegreerde toestemmingsstap versterkt dit.",
        },
      ],
    },
    {
      heading: "14. Datalekken",
      blocks: [
        {
          type: "p",
          text: "Indien zich een inbreuk in verband met persoonsgegevens voordoet, zal Photohawk de betrokken verwerkingsverantwoordelijke zonder onnodige vertraging na kennisname daarvan op de hoogte stellen, en hem bijstaan bij het nakomen van zijn eigen kennisgevingsverplichtingen aan toezichthoudende autoriteiten en betrokkenen (Art. 33–34 GDPR).",
        },
      ],
    },
    {
      heading: "15. Wijzigingen in dit beleid",
      blocks: [
        {
          type: "p",
          text: "Wij kunnen dit beleid bijwerken; de “Ingangsdatum” hierboven toont de huidige versie. Wezenlijke wijzigingen worden aan accounthouders meegedeeld.",
        },
      ],
    },
    {
      heading: "16. Contact",
      blocks: [
        {
          type: "p",
          text: "Functionaris voor gegevensbescherming — Andrew Hammond. CLOUDCONCUR LTD (bedrijfsnr. 13399913), handelend onder de naam Photohawk. E-mail: support@photohawk.com · 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Verenigd Koninkrijk.",
        },
      ],
    },
  ],
};

export default gdprNl;
